// @ts-nocheck
/*
 * Port of tts-plans/list.js (579 lines). BYTE-IDENTICAL.
 * TTS plans list with two modals:
 *   - Edit modal (patch plan_name/per_character/per_variable; only shown
 *     when `!is_allocated`)
 *   - Companies modal (list of companies currently on the plan)
 * Plan status is a bootstrap `slider_switch` checkbox â€” turning OFF
 * removes the row from the local list (matches v1's behaviour: v1 hides
 * disabled plans since the endpoint only returns enabled ones on next
 * load).
 * PrimeReact DataTable â†’ obd3-gui DataTable + tanstack ColumnDef.
 * Component export name is `VoicePlanList` (v1 filename ambiguity from
 * the tts-plans-copied-from-voice-plan history â€” kept to match v1).
 */
import React, { useState, useEffect } from 'react';
import { getApiCall, patchApiCallWithHeader } from '@/service/httpCall';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { API_TTS_PLANS_LIST, API_TTS_PLANS_UPDATE } from '@/service/url';
import { ROUTES } from '@/utilities/constant';
import Loader from '@/components/partials/components/Loader';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';
import { useNavigate } from 'react-router-dom';
import { getUser } from '@/utilities/setting';
import { USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';

const TTS_PLAN_BLOCKED_ROLES = [
  USER_ROLES.CALL_CENTRE_ADMIN,
  USER_ROLES.CALL_CENTRE_TEAM_LEAD,
  USER_ROLES.CALL_CENTRE_AGENT,
  USER_ROLES.CALL_CENTRE,
];
import { FiCheckCircle, FiXCircle, FiEdit2, FiEye } from 'react-icons/fi';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Download, Plus } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const MAX_TTS_PRICE_DECIMALS = 4;
const TTS_UNIT_RUPEES = 'rupees';
const TTS_UNIT_PAISE = 'paise';

function sanitizeTtsPriceInput(raw: string, allowDecimals: boolean): string | null {
  const value = String(raw).replace(/[+\-eE]/g, '');
  if (value === '') return '';
  if (allowDecimals) {
    if (!/^\d*\.?\d*$/.test(value)) return null;
    const decimalParts = value.split('.');
    if (decimalParts.length > 1 && decimalParts[1].length > MAX_TTS_PRICE_DECIMALS) {
      return null;
    }
  } else {
    if (!/^\d+$/.test(value)) return null;
  }
  if (value !== '.' && Number(value) > 1000) {
    return null;
  }
  return value;
}

function toWholeTtsPrice(value: any) {
  if (value === '' || value === '.' || value == null) return '';
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n) || n <= 0) return '';
  return String(Math.min(n, 1000));
}

function isPositiveTtsPrice(value: any, allowDecimals = true) {
  const num = Number(value);
  if (value === '' || value === '.' || !Number.isFinite(num) || num <= 0 || num > 1000) {
    return false;
  }
  if (!allowDecimals && (String(value).includes('.') || !Number.isInteger(num))) {
    return false;
  }
  return true;
}

function formatUnitBalance(unit: any) {
  return unit === TTS_UNIT_PAISE ? 'Paise' : 'Rupees';
}

const VoicePlanList = () => {
  const navigate = useNavigate();
  const [statusList, setStatusList] = useState<any>({});
  const [toastProps, setToastProps] = useState<any>(null);
  const [showToast, setShowToast] = useState(false);
  const [voicePlan, setVoicePlan] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [searchValue, setSearchValue] = useState<any>(null);
  const [originalDataList, setOriginalDataList] = useState<any[]>([]);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [editPlanName, setEditPlanName] = useState('');
  const [editPerChar, setEditPerChar] = useState<any>('');
  const [editPerVar, setEditPerVar] = useState<any>('');
  const [editUnitBalance, setEditUnitBalance] = useState(TTS_UNIT_RUPEES);
  const [editSaving, setEditSaving] = useState(false);
  const [showCompaniesModal, setShowCompaniesModal] = useState(false);
  const [companiesToShow, setCompaniesToShow] = useState<any[]>([]);
  const [companiesModalPlanName, setCompaniesModalPlanName] = useState('');
  const editAllowDecimals = editUnitBalance === TTS_UNIT_PAISE;

  const handleExport = async () => {
    try {
      setButtonDisable(true);

      exportToCSV({
        fileName: 'tts-plan-list',
        data: voicePlan,
        columns: [
          { header: 'Plan ID', accessor: 'id' },
          { header: 'Plan Name', accessor: 'plan_name' },
          { header: 'Per Character', accessor: 'per_character' },
          { header: 'Per Variable', accessor: 'per_variable' },
          { header: 'Unit Balance', accessor: (row: any) => formatUnitBalance(row.unit_balance) },
          {
            header: 'Created By',
            accessor: (row: any) =>
              row.createdBy?.username || row.createdBy?.name || '-',
          },
          {
            header: 'Allocated',
            accessor: (row: any) => (row.is_allocated ? 'Yes' : 'No'),
          },
          {
            header: 'Companies',
            accessor: (row: any) =>
              row.assignedCompanies && row.assignedCompanies.length > 0
                ? row.assignedCompanies.map((c: any) => c.name).join(', ')
                : '-',
          },
          {
            header: 'Created Date',
            accessor: (row: any) =>
              new Date(row.req_date).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
          {
            header: 'Plan Status',
            accessor: (row: any) =>
              (statusList[row.id] ?? row.plan_status == 1)
                ? 'Enabled'
                : 'Disabled',
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const handleEditClick = (plan: any) => {
    setEditingPlan(plan);
    setEditPlanName(plan.plan_name);
    setEditPerChar(
      plan.unit_balance === TTS_UNIT_PAISE ? plan.per_character : toWholeTtsPrice(plan.per_character),
    );
    setEditPerVar(
      plan.unit_balance === TTS_UNIT_PAISE ? plan.per_variable : toWholeTtsPrice(plan.per_variable),
    );
    setEditUnitBalance(plan.unit_balance === TTS_UNIT_PAISE ? TTS_UNIT_PAISE : TTS_UNIT_RUPEES);
  };

  const handleEditClose = () => {
    setEditingPlan(null);
    setEditPlanName('');
    setEditPerChar('');
    setEditPerVar('');
    setEditUnitBalance(TTS_UNIT_RUPEES);
  };

  const handleEditSave = async () => {
    if (!editPlanName.trim()) {
      toast.error('Plan name is required');
      return;
    }
    if (!isPositiveTtsPrice(editPerChar, editAllowDecimals)) {
      toast.error(
        editAllowDecimals
          ? 'Per Character must be greater than 0 and at most 1000'
          : 'Rupees cannot have decimals. Enter a whole number for Per Character',
      );
      return;
    }
    if (!isPositiveTtsPrice(editPerVar, editAllowDecimals)) {
      toast.error(
        editAllowDecimals
          ? 'Per Variable must be greater than 0 and at most 1000'
          : 'Rupees cannot have decimals. Enter a whole number for Per Variable',
      );
      return;
    }

    setEditSaving(true);
    try {
      const url = API_TTS_PLANS_UPDATE.replace(':id', editingPlan.id);
      await patchApiCallWithHeader(url, {
        plan_name: editPlanName.trim(),
        per_character: Number(editPerChar),
        per_variable: Number(editPerVar),
        unit_balance: editUnitBalance,
        plan_status: editingPlan.plan_status,
      });
      toast.success('TTS plan updated successfully');
      const updated = {
        ...editingPlan,
        plan_name: editPlanName.trim(),
        per_character: Number(editPerChar),
        per_variable: Number(editPerVar),
        unit_balance: editUnitBalance,
      };
      setVoicePlan((prev: any[]) =>
        prev.map((p: any) => (p.id === editingPlan.id ? updated : p)),
      );
      setOriginalDataList((prev: any[]) =>
        prev.map((p: any) => (p.id === editingPlan.id ? updated : p)),
      );
      handleEditClose();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to update TTS plan');
    } finally {
      setEditSaving(false);
    }
  };

  const fetchVoicePlans = async () => {
    setLoading(true);
    try {
      getApiCall(API_TTS_PLANS_LIST)
        .then(onVoicePlanSuccess)
        .catch(onError)
        .finally(() => setLoading(false));
    } catch (error) {
      console.error('Error fetching voice plans:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = getUser();
    if (user.role === USER_ROLES.SUPERADMIN) {
      setIsSuperadmin(true);
    }
    if (user?.acl_settings) {
      setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    }
    fetchVoicePlans();
  }, []);

  function onVoicePlanSuccess(resp: any) {
    const updatedList = resp.map((val: any, idx: number) => {
      val['key'] = idx + 1;
      val['req_date'] = new Date(val['req_date']).toGMTString();
      return val;
    });
    setVoicePlan(updatedList);
    setOriginalDataList(updatedList);
  }

  useEffect(() => {
    if (searchValue) {
      setVoicePlan(
        originalDataList.filter((data: any) =>
          data.plan_name.toLowerCase().includes(searchValue.trim().toLowerCase()),
        ),
      );
    } else {
      setVoicePlan(originalDataList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    const user = getUser();
    if (user) {
      if (user?.acl_settings) {
        setModuleOperationsForReadPermissionCheck(
          parseModuleOperations(user.acl_settings.module_operations),
        );
      }
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  function onError(err: any) {
    toast.error(friendlyApiError(err) || 'Failed to load TTS plans');
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleCreateButtonClick = () => {
    navigate(`/${ROUTES.TTSPLANS.CREATE}`);
  };

  const isCallCentreScopedRole = TTS_PLAN_BLOCKED_ROLES.includes(getUser()?.role);
  const canAdd =
    !isCallCentreScopedRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.TTS_PLANS, 'add');
  const canEdit =
    !isCallCentreScopedRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.TTS_PLANS, 'edit');
  const canDelete =
    !isCallCentreScopedRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.TTS_PLANS, 'delete');
  const canRead =
    !isCallCentreScopedRole &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.TTS_PLANS, 'read');

  if (!canRead) {
    return (
      <div
        className="text-center p-4"
        style={{ marginTop: '50px', marginBottom: '50px', fontSize: '16px' }}
      >
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) {
    return <Loader />;
  }

  const handleStatusToggle = async (planId: any, currentStatus: any, assignedCount = 0) => {
    const newStatus = !currentStatus;
    if (!newStatus && assignedCount > 0) {
      toast.error(`Plan is assigned to ${assignedCount} compan${assignedCount === 1 ? 'y' : 'ies'} and cannot be disabled`);
      return;
    }

    try {
      const url = API_TTS_PLANS_UPDATE.replace(':id', planId);
      await patchApiCallWithHeader(url, {
        plan_status: newStatus ? 1 : 0,
      });

      if (!newStatus) {
        setVoicePlan((prev: any[]) => prev.filter((plan: any) => plan.id !== planId));
        setOriginalDataList((prev: any[]) =>
          prev.filter((plan: any) => plan.id !== planId),
        );
      } else {
        setVoicePlan((prev: any[]) =>
          prev.map((plan: any) =>
            plan.id === planId ? { ...plan, plan_status: 1 } : plan,
          ),
        );
      }

      toast.success(`Plan status updated to ${newStatus ? 'Enable' : 'Disable'}`);
    } catch (error) {
      toast.error(friendlyApiError(error) || 'Failed to update plan status');
    }
  };

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'id',
      header: 'Plan ID',
      size: 90,
      cell: ({ row }: any) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.id}
        </span>
      ),
    },
    {
      accessorKey: 'plan_name',
      header: 'Plan Name',
      cell: ({ row }: any) => (
        <span className="font-medium text-foreground">{row.original.plan_name}</span>
      ),
    },
    {
      accessorKey: 'per_character',
      header: 'Per Character',
      cell: ({ row }: any) => (
        <span className="font-mono tabular-nums text-sm text-foreground">
          {row.original.per_character}
        </span>
      ),
    },
    {
      accessorKey: 'per_variable',
      header: 'Per Variable',
      cell: ({ row }: any) => (
        <span className="font-mono tabular-nums text-sm text-foreground">
          {row.original.per_variable}
        </span>
      ),
    },
    {
      accessorKey: 'unit_balance',
      header: 'Unit Balance',
      cell: ({ row }: any) => (
        <span className="text-sm text-foreground">
          {formatUnitBalance(row.original.unit_balance)}
        </span>
      ),
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: ({ row }: any) => (
        <span className="text-sm text-foreground">
          {row.original?.createdBy?.username ||
            row.original?.createdBy?.name ||
            '—'}
        </span>
      ),
    },
    {
      id: 'is_allocated',
      header: 'Allocated',
      size: 100,
      cell: ({ row }: any) =>
        row.original?.is_allocated ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
            <FiCheckCircle className="h-3 w-3" />
            Yes
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted/60 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground ring-1 ring-inset ring-border/60">
            <FiXCircle className="h-3 w-3" />
            No
          </span>
        ),
    },
    {
      id: 'companies',
      header: 'Companies',
      cell: ({ row }: any) =>
        row.original?.assignedCompanies &&
        row.original.assignedCompanies.length > 0 ? (
          <button
            type="button"
            onClick={() => {
              setCompaniesToShow(row.original.assignedCompanies);
              setCompaniesModalPlanName(row.original.plan_name);
              setShowCompaniesModal(true);
            }}
            className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 cursor-pointer"
            title="View companies using this plan"
          >
            <FiEye className="h-3 w-3" />
            {row.original.assignedCompanies.length}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'req_date',
      header: 'Created Date',
      cell: ({ row }: any) => (
        <span className="text-xs text-muted-foreground">
          {new Date(row.original?.req_date).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 100,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }: any) => {
        const rowData = row.original;
        const isChecked = statusList[rowData.id] ?? rowData.plan_status == 1;
        return (
          <div className="flex items-center gap-1.5">
            {canEdit && !rowData?.is_allocated && (
              <button
                type="button"
                onClick={() => handleEditClick(rowData)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 cursor-pointer"
                title="Edit plan"
                aria-label="Edit plan"
              >
                <FiEdit2 className="h-3.5 w-3.5" />
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                onClick={() => handleStatusToggle(rowData.id, isChecked, rowData.assignedCompanies?.length || 0)}
                aria-pressed={isChecked}
                aria-label={isChecked ? 'Disable plan' : 'Enable plan'}
                title={isChecked ? 'Disable plan' : 'Enable plan'}
                className={
                  'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors ring-1 ring-inset ' +
                  (isChecked
                    ? 'bg-emerald-500 ring-emerald-600'
                    : 'bg-muted/70 ring-border/70')
                }
              >
                <span
                  className={
                    'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
                    (isChecked ? 'translate-x-[18px]' : 'translate-x-[2px]')
                  }
                />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="TTS Plans"
        description="Pricing plans for text-to-speech usage per company."
        actions={
          <>
            {voicePlan.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={buttonDisable}>
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <Button onClick={handleCreateButtonClick}>
                <Plus className="h-4 w-4" />
                Create TTS Plan
              </Button>
            )}
          </>
        }
      />
      <DataTable
        tableId="tts-plans:list:v1"
        columns={columns}
        data={voicePlan}
        pageSize={10}
        searchPlaceholder="Search by plan name…"
        emptyTitle="No TTS plans"
        emptyDescription="No TTS plans match the current filters."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        disableExport
      />

      {/* Edit TTS Plan modal */}
      <Modal show={!!editingPlan} onHide={handleEditClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <FiEdit2 className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">Edit TTS Plan</div>
                <div className="text-xs font-normal text-muted-foreground">
                  Adjust the per-character / per-variable pricing for this plan.
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Plan Name <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={editPlanName}
                onChange={(e) => {
                  if (/^[a-zA-Z0-9 ]*$/.test(e.target.value))
                    setEditPlanName(e.target.value);
                }}
                placeholder="Plan Name"
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Unit Balance <span className="text-destructive">*</span>
              </label>
              <select
                value={editUnitBalance}
                onChange={(e) => {
                  const next = e.target.value;
                  setEditUnitBalance(next);
                  if (next === TTS_UNIT_RUPEES) {
                    setEditPerChar((prev: any) => toWholeTtsPrice(prev));
                    setEditPerVar((prev: any) => toWholeTtsPrice(prev));
                  }
                }}
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              >
                <option value={TTS_UNIT_RUPEES}>Rupees</option>
                <option value={TTS_UNIT_PAISE}>Paise</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Per Character <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                inputMode={editAllowDecimals ? 'decimal' : 'numeric'}
                value={editPerChar}
                onChange={(e) => {
                  const next = sanitizeTtsPriceInput(e.target.value, editAllowDecimals);
                  if (next !== null) setEditPerChar(next);
                }}
                onKeyDown={(e) => {
                  const blocked = editAllowDecimals
                    ? ['e', 'E', '+', '-']
                    : ['e', 'E', '+', '-', '.'];
                  if (blocked.includes(e.key)) e.preventDefault();
                }}
                placeholder={editAllowDecimals ? 'e.g. 0.05' : '1 – 1000'}
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Per Variable <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                inputMode={editAllowDecimals ? 'decimal' : 'numeric'}
                value={editPerVar}
                onChange={(e) => {
                  const next = sanitizeTtsPriceInput(e.target.value, editAllowDecimals);
                  if (next !== null) setEditPerVar(next);
                }}
                onKeyDown={(e) => {
                  const blocked = editAllowDecimals
                    ? ['e', 'E', '+', '-']
                    : ['e', 'E', '+', '-', '.'];
                  if (blocked.includes(e.key)) e.preventDefault();
                }}
                placeholder={editAllowDecimals ? 'e.g. 0.05' : '1 – 1000'}
                className="input-embossed h-10 w-full rounded-md px-3 text-sm"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={handleEditClose} disabled={editSaving}>
            Cancel
          </Button>
          <Button onClick={handleEditSave} disabled={editSaving || !editPlanName.trim() || !isPositiveTtsPrice(editPerChar, editAllowDecimals) || !isPositiveTtsPrice(editPerVar, editAllowDecimals)} className="gap-1.5">
            {editSaving && (
              <span
                className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
                aria-hidden
              />
            )}
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Companies modal */}
      <Modal
        show={showCompaniesModal}
        onHide={() => setShowCompaniesModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <FiEye className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground">
                  Assigned Companies
                </div>
                <div className="text-xs font-normal text-muted-foreground">
                  Companies currently using{' '}
                  <span className="font-semibold text-foreground">
                    "{companiesModalPlanName}"
                  </span>
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {companiesToShow.length > 0 ? (
            <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
              {companiesToShow.map((c: any, idx: number) => (
                <div
                  key={c.id}
                  className={
                    'flex items-center gap-2 px-4 py-2.5 text-sm text-foreground ' +
                    (idx > 0 ? 'border-t border-border/40' : '')
                  }
                >
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-[10px] font-semibold text-primary ring-1 ring-inset ring-primary/20 tabular-nums">
                    {idx + 1}
                  </span>
                  <span className="truncate">{c.name}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-border/60 bg-muted/30 p-6 text-center text-sm text-muted-foreground">
              No companies assigned.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={() => setShowCompaniesModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </FillPage>
  );
};

export default VoicePlanList;
